import { analytics } from "@/services/analytics.service";
import { tokenManager } from "@/utils/token-manager";

const GOOGLE_ANALYTICS_MEASUREMENT_ID = "G-MPCF9E5632";

type GoogleAnalyticsCommand = IArguments | unknown[];
export type GoogleAnalyticsEventParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer?: GoogleAnalyticsCommand[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface GoogleAnalyticsPageView {
  pathname: string;
  search: string;
  title?: string;
}

class AnalyticsBootstrapService {
  private googleAnalyticsReady: Promise<void> | null = null;

  public async initializeAnalytics(): Promise<boolean> {
    analytics.optIn();
    return true;
  }

  /**
   * GA is intentionally limited to logged-out marketing surfaces. Callers own
   * the route/auth gate before invoking this method.
   */
  public trackGoogleAnalyticsPageView({ pathname, search, title }: GoogleAnalyticsPageView): void {
    if (typeof window === "undefined") return;

    this.initializeGoogleAnalytics()
      .then(() => {
        const pagePath = `${pathname}${search}`;
        window.gtag?.("event", "page_view", {
          page_location: `${window.location.origin}${pagePath}`,
          page_path: pagePath,
          page_title: title ?? document.title,
        });
      })
      .catch(error => {
        console.warn("[analytics] Google Analytics initialization failed", error);
      });
  }

  /**
   * Fires a GA event only when GA was already bootstrapped by a route-gated
   * page view AND the visitor is still logged out. The bootstrap check alone
   * isn't enough: a user can log in mid-session (SPA navigation, no reload)
   * after a marketing page loaded GA, so auth is re-checked at event time
   * against tokenManager — the same storage-backed session state AuthContext
   * derives `isAuthenticated` from. Events never load GA themselves. The gtag
   * stub queues to dataLayer, so events racing the script load are safe.
   */
  public trackGoogleAnalyticsEvent(name: string, params: GoogleAnalyticsEventParams = {}): void {
    if (typeof window === "undefined") return;
    if (!this.googleAnalyticsReady) return;
    if (tokenManager.hasValidSession()) return;

    window.gtag?.("event", name, params);
  }

  private initializeGoogleAnalytics(): Promise<void> {
    if (typeof window === "undefined") return Promise.resolve();
    if (this.googleAnalyticsReady) return this.googleAnalyticsReady;

    this.googleAnalyticsReady = new Promise((resolve, reject) => {
      window.dataLayer = window.dataLayer || [];
      if (!window.gtag) {
        window.gtag = function gtag() {
          // Google Analytics' bootstrap stub expects the arguments object here.
          // eslint-disable-next-line prefer-rest-params
          window.dataLayer?.push(arguments);
        };
      }
      window.gtag("js", new Date());
      window.gtag("config", GOOGLE_ANALYTICS_MEASUREMENT_ID, { send_page_view: false });

      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}"]`,
      );
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google Analytics tag"));
      document.head.appendChild(script);
    });

    return this.googleAnalyticsReady;
  }
}

export const analyticsBootstrapService = new AnalyticsBootstrapService();
